import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { ProColumns, ProTable, ActionType } from "@ant-design/pro-components";
import { App, Popconfirm, Typography, Button, Select, Space } from "antd";

import dayjs from "../../../config/dayjs";
import { claimStatusLabels, claimTypeLabels } from "../../../constant/enum";
import { getUser } from "../../../services/auth";
import { removeEmptyParams } from "../../../utils/listQueryHelper";
import { waitTime } from "../../../utils/WaitTime";
import {
  archiveClaim,
  downloadClaimFileFromS3,
  getClaims,
  getUserClaims,
  updateClaimStatus,
} from "../api/claimAPI";
import { IClaimResponse } from "../type/claim";

const { Text } = Typography;

export const ClaimTable = () => {
  const navigate = useNavigate();
  const actionRef = useRef<ActionType>();
  const { message } = App.useApp();
  const user = getUser();
  const role = user.role;
  const [isActionRequired, setIsActionRequired] = useState(false);

  const ActionCell: React.FC<{ record: IClaimResponse }> = ({ record }) => {
    const [selectedAction, setSelectedAction] = useState(record.status);

    const statusOptions = [
      { label: "Pending", value: "pending", disabled: true },
      { label: "Approve", value: "approved" },
      { label: "Reject", value: "rejected" },
    ];

    return (
      <Space direction="horizontal">
        <Select
          defaultValue={selectedAction}
          onChange={setSelectedAction}
          options={statusOptions}
        />
        <Popconfirm
          title={`Are you sure you want to ${selectedAction} this claim?`}
          description="This action cannot be undone."
          onConfirm={async () => {
            try {
              await updateClaimStatus(record.id, { status: selectedAction });
              message.success("Claim status updated successfully");
              actionRef.current?.reload();
            } catch (error: any) {
              message.error(error.message || "Failed to update claim status");
            }
          }}
          disabled={role !== "admin" && role !== "branchAdmin"}
        >
          <Button
            type="primary"
            style={{ borderRadius: 4 }}
            disabled={
              (role !== "admin" && role !== "branchAdmin") ||
              record.status === "approved" ||
              record.status === "rejected" ||
              selectedAction === record.status
            }
          >
            Confirm
          </Button>
        </Popconfirm>
      </Space>
    );
  };

  const columns: ProColumns<IClaimResponse>[] = [
    {
      title: "No",
      dataIndex: "id",
      key: "id",
      search: false,
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      search: role === "admin" || role === "branchAdmin" ? true : false,
      render: (_, record) => <span>{record.user.name}</span>,
    },
    {
      title: "Claim Type",
      dataIndex: "claimType",
      key: "claimType",
      valueType: "select",
      valueEnum: claimTypeLabels,
      render: (_, record) => {
        const claimType = record.claimType;
        const claimTypeLabel =
          claimTypeLabels[claimType as keyof typeof claimTypeLabels] ??
          claimType;

        return <span>{claimTypeLabel}</span>;
      },
      fieldProps: {
        showSearch: true,
        placeholder: "Search claim type",
        filterOption: (
          input: string,
          option?: { label: string; value: string }
        ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
      },
      search: {
        transform: (value) => ({ claimType: value }),
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      valueType: "date",
      search: false,
      sorter: true,
      render: (_, record) => {
        if (record.startDate) {
          const date = dayjs(record.startDate).format("DD/MM/YYYY");
          const time = dayjs(record.startDate).format("HH:mm A");
          return `${date} ${time}`;
        } else {
          return "-";
        }
      },
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      valueType: "date",
      search: false,
      sorter: true,
      render: (_, record) => {
        if (record.endDate) {
          const date = dayjs(record.endDate).format("DD/MM/YYYY");
          const time = dayjs(record.endDate).format("HH:mm A");
          return `${date} ${time}`;
        } else {
          return "-";
        }
      },
    },
    {
      title: "Claim Amount (RM)",
      dataIndex: "claimAmount",
      key: "claimAmount",
      valueType: "digit",
      search: false,
      align: "center",
      sorter: true,
      render: (_, record) => {
        if (
          record.claimType === "medical" &&
          (role === "admin" || role === "branchAdmin")
        ) {
          const balance = record.user.claimBalance?.medical?.balance ?? 0;
          const claimAmount = record.claimAmount ?? 0;
          return `${claimAmount} (Balance: ${balance})`;
        }
        return record.claimAmount ?? "-";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      valueType: "select",
      valueEnum: claimStatusLabels,
      width: 100,
      align: "center",
      sorter: true,
      render: (_, record) => {
        const status = record.status;
        const statusLabel =
          claimStatusLabels[status as keyof typeof claimStatusLabels] ?? status;

        return (
          <Text
            type={
              status === "pending"
                ? "warning"
                : status === "approved"
                ? "success"
                : "danger"
            }
          >
            {statusLabel}
          </Text>
        );
      },
      fieldProps: {
        showSearch: true,
        placeholder: "Search status",
        filterOption: (
          input: string,
          option?: { label: string; value: string }
        ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
      },
      search: {
        transform: (value) => ({ status: value }),
      },
    },
    {
      title: "Attachment",
      dataIndex: "fileUrl",
      key: "fileUrl",
      valueType: "text",
      search: false,
      align: "center",
      render: (_, record) => {
        const handleFileDownload = async (fileUrl: string) => {
          try {
            await downloadClaimFileFromS3(record.id, fileUrl);
          } catch (error) {
            message.error("Failed to open the file");
          }
        };

        const getFileName = (url: string) => {
          const decodedUrl = decodeURIComponent(url);
          const fileName = decodedUrl.split("/").pop() || url;
          return fileName;
        };

        if (record.fileUrl && record.fileUrl.length > 0) {
          return (
            <Space direction="vertical" size="small">
              {record.fileUrl.map((fileUrl, index) => (
                <Button
                  key={index}
                  type="link"
                  onClick={() => handleFileDownload(fileUrl)}
                  style={{ padding: 0, height: "auto" }}
                >
                  {getFileName(fileUrl)}
                </Button>
              ))}
            </Space>
          );
        } else {
          return <span>-</span>;
        }
      },
    },
    ...(isActionRequired && (role === "admin" || role === "branchAdmin")
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            search: false,
            align: "center" as const,
            render: (_: any, record: IClaimResponse) => {
              if (record.status === "pending") {
                return <ActionCell record={record} />;
              } else {
                return null;
              }
            },
          },
        ]
      : []),
    ...(role === "staff" || role === "lawyer"
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            search: false,
            align: "center" as const,
            render: (_: any, record: IClaimResponse) => (
              <Space>
                <EditOutlined
                  style={{
                    color: record.status === "pending" ? "blue" : "grey",
                    cursor:
                      record.status === "pending" ? "pointer" : "not-allowed",
                  }}
                  onClick={() =>
                    record.status === "pending" &&
                    navigate(`/claims/${record.id}/edit`, {
                      state: {
                        claimId: record.id,
                        displayName: record.claimType,
                      },
                    })
                  }
                />
                <Popconfirm
                  title="Are you sure you want to delete this claim?"
                  description="This action cannot be undone."
                  onConfirm={async () => {
                    try {
                      const response = await archiveClaim(record.id);
                      message.success(response.message);
                      actionRef.current?.reload();
                    } catch (error: any) {
                      message.error(error.message);
                    }
                  }}
                  disabled={record.status !== "pending"}
                >
                  <DeleteOutlined
                    style={{
                      color: record.status === "pending" ? "red" : "grey",
                      cursor:
                        record.status === "pending" ? "pointer" : "not-allowed",
                    }}
                  />
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <ProTable<IClaimResponse>
        cardBordered
        form={{ layout: "vertical" }}
        scroll={{ x: "max-content" }}
        request={async (params, sort, filter) => {
          try {
            params = removeEmptyParams(params);
            if (role === "admin" || role === "branchAdmin") {
              const data = await getClaims({ filter, sort, params });
              const claims = data.results;
              // Check if there are any pending claims and update state accordingly
              const hasPending = claims.some(
                (claim) => claim.status === "pending"
              );
              setIsActionRequired(hasPending);

              return {
                data: claims,
                success: true,
                total: data.totalResults,
              };
            } else {
              const data = await getUserClaims(user.id, {
                filter,
                sort,
                params,
              });
              const claims = data.results;
              // Check if there are any pending claims and update state accordingly
              const hasPending = claims.some(
                (claim) => claim.status === "pending"
              );
              setIsActionRequired(hasPending);

              return {
                data: claims,
                success: true,
                total: data.totalResults,
              };
            }
          } catch (error) {
            return {
              data: [],
              success: false,
              total: 10,
            };
          }
        }}
        rowKey={(data) => data.id}
        columns={columns}
        editable={{
          type: "multiple",
          onSave: async (_rowKey, _data, _row) => {
            await waitTime(2000);
          },
        }}
        actionRef={actionRef}
      />
    </>
  );
};
